import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { DiscgoLogo } from '../components/DiscgoLogo';

const PLAYER_WEB_URL = import.meta.env.VITE_PLAYER_WEB_URL || 'http://localhost:5000';
const OWNER_WEB_URL = import.meta.env.VITE_OWNER_WEB_URL || 'http://localhost:5001';
const WAITLIST_MODE = import.meta.env.VITE_WAITLIST_MODE === 'true';

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','District of Columbia','Florida','Georgia','Hawaii','Idaho','Illinois',
  'Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts',
  'Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada',
  'New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota',
  'Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina',
  'South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington',
  'West Virginia','Wisconsin','Wyoming',
];
const CA_PROVINCES = [
  'Alberta','British Columbia','Manitoba','New Brunswick',
  'Newfoundland and Labrador','Nova Scotia','Ontario','Prince Edward Island',
  'Quebec','Saskatchewan','Northwest Territories','Nunavut','Yukon',
];
const COUNTRIES = [
  'United States','Canada','Australia','Austria','Belgium','Brazil','Chile',
  'Croatia','Czech Republic','Denmark','Estonia','Finland','France','Germany',
  'Hungary','Iceland','Ireland','Italy','Japan','Latvia','Lithuania','Mexico',
  'Netherlands','New Zealand','Norway','Poland','Portugal','Slovakia','Slovenia',
  'Spain','Sweden','Switzerland','United Kingdom','Other',
];

export default function AuthPage() {
  const { signIn, signUp, signOut, session, loading: authLoading, roles } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const signoutParam = searchParams.get('signout');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>(() => {
    const tab = searchParams.get('tab');
    return tab === 'signup' ? 'signup' : 'login';
  });

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupFullName, setSignupFullName] = useState('');
  const [signupRole, setSignupRole] = useState<'player' | 'owner'>('player');
  const [signupCourseName, setSignupCourseName] = useState('');
  const [signupHoles, setSignupHoles] = useState('18');
  const [signupAddress, setSignupAddress] = useState('');
  const [signupCity, setSignupCity] = useState('');
  const [signupState, setSignupState] = useState('');
  const [signupCountry, setSignupCountry] = useState('United States');
  const [signupPlayerState, setSignupPlayerState] = useState(
    () => searchParams.get('state') || ''
  );
  const [waitlistConfirmed, setWaitlistConfirmed] = useState(
    () => searchParams.get('waitlisted') === '1'
  );

  const buildRedirectUrl = (baseUrl: string) => {
    if (!session) return baseUrl;
    // Use query params (not hash) so Supabase's detectSessionInUrl doesn't intercept
    // and consume the tokens before our explicit setSession call in the target app.
    const params = new URLSearchParams({
      sb_access_token: session.access_token,
      sb_refresh_token: session.refresh_token,
    });
    return `${baseUrl}?${params.toString()}`;
  };

  const redirectParam = searchParams.get('redirect');

  useEffect(() => {
    if (signoutParam === '1') {
      signOut();
      return;
    }

    if (authLoading) return;

    if (session && roles.length > 0) {
      // If there's an in-app redirect (e.g. /book/:courseId), go there first
      if (redirectParam) {
        navigate(redirectParam);
        return;
      }

      if (roles.length === 1) {
        const singleRole = roles[0];
        if (singleRole === 'player') {
          window.location.href = buildRedirectUrl(PLAYER_WEB_URL);
        } else if (singleRole === 'owner' || singleRole === 'admin') {
          window.location.href = buildRedirectUrl(OWNER_WEB_URL);
        }
      } else {
        navigate('/role-selector');
      }
    }
  }, [signoutParam, session, roles, authLoading, navigate, redirectParam]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    const { error } = await signIn(loginEmail, loginPassword);

    if (error) {
      setError(error.message);
    }

    setLoading(false);
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    const courseData = signupRole === 'owner' ? {
      name: signupCourseName,
      holes: parseInt(signupHoles, 10),
      address: signupAddress,
      city: signupCity,
      state: signupState,
      country: signupCountry,
    } : undefined;

    const { error } = await signUp(
      signupEmail, signupPassword, signupFullName, signupRole, courseData,
      WAITLIST_MODE && signupRole === 'player' ? signupPlayerState : undefined,
    );

    if (error) {
      setError(error.message);
    } else if (WAITLIST_MODE && signupRole === 'player') {
      setWaitlistConfirmed(true);
    } else {
      setSuccessMessage('Account created! Please check your email to verify your account.');
      setSignupEmail('');
      setSignupPassword('');
      setSignupFullName('');
      setSignupRole('player');
      setSignupCourseName('');
      setSignupHoles('18');
      setSignupCity('');
      setSignupState('');
      setSignupCountry('');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <DiscgoLogo style={{ height: '70px', width: '70px' }} />
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="p-6 pb-4">
            <h2 className="text-2xl text-center text-foreground font-bold lowercase">welcome to discgo</h2>
            <p className="text-center text-muted-foreground mt-2 lowercase">
              connecting disc golf players with private courses
            </p>
          </div>

          <div className="border-b">
            <div className="flex">
              <button
                onClick={() => setActiveTab('login')}
                className={`flex-1 px-4 py-3 font-medium transition-colors lowercase ${
                  activeTab === 'login'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                login
              </button>
              <button
                onClick={() => setActiveTab('signup')}
                className={`flex-1 px-4 py-3 font-medium transition-colors lowercase ${
                  activeTab === 'signup'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                sign up
              </button>
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'login' && (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="login-email" className="block text-sm font-medium text-foreground lowercase">
                    email
                  </label>
                  <input
                    id="login-email"
                    type="email"
                    placeholder="you@example.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="login-password" className="block text-sm font-medium text-foreground lowercase">
                    password
                  </label>
                  <input
                    id="login-password"
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                </div>
                {error && (
                  <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  className="w-full px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-full font-medium transition-all duration-300 hover:shadow-lg hover:shadow-primary/25 lowercase"
                  disabled={loading}
                >
                  {loading ? 'logging in...' : 'login'}
                </button>
              </form>
            )}

            {activeTab === 'signup' && waitlistConfirmed && (
              <div className="p-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg space-y-2">
                <p className="font-semibold lowercase">thanks for signing up!</p>
                <p className="lowercase">we'll notify you as soon as discgo launches in {signupPlayerState}.</p>
                <p className="lowercase">keep an eye on your inbox at {signupEmail}.</p>
              </div>
            )}

            {activeTab === 'signup' && !waitlistConfirmed && (
              <form onSubmit={handleSignup} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="signup-name" className="block text-sm font-medium text-foreground lowercase">
                    full name
                  </label>
                  <input
                    id="signup-name"
                    type="text"
                    placeholder="john doe"
                    value={signupFullName}
                    onChange={(e) => setSignupFullName(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="signup-email" className="block text-sm font-medium text-foreground lowercase">
                    email
                  </label>
                  <input
                    id="signup-email"
                    type="email"
                    placeholder="you@example.com"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="signup-password" className="block text-sm font-medium text-foreground lowercase">
                    password
                  </label>
                  <input
                    id="signup-password"
                    type="password"
                    placeholder="at least 6 characters"
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                </div>
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-foreground lowercase">i'm signing up as a:</label>
                  <div className="space-y-2">
                    <label
                      className="flex items-center gap-4 cursor-pointer p-4 border rounded-full hover:bg-accent transition-all"
                      style={{
                        borderColor: signupRole === 'player' ? 'var(--primary)' : '#e5e7eb',
                        backgroundColor: signupRole === 'player' ? 'var(--accent)' : 'transparent',
                      }}
                    >
                      <input
                        type="radio"
                        name="signup-role"
                        value="player"
                        checked={signupRole === 'player'}
                        onChange={(e) => setSignupRole(e.target.value as 'player')}
                        className="w-4 h-4"
                      />
                      <div className="font-medium text-foreground lowercase">player</div>
                    </label>
                    <label
                      className="flex items-center gap-4 cursor-pointer p-4 border rounded-full hover:bg-accent transition-all"
                      style={{
                        borderColor: signupRole === 'owner' ? 'var(--primary)' : '#e5e7eb',
                        backgroundColor: signupRole === 'owner' ? 'var(--accent)' : 'transparent',
                      }}
                    >
                      <input
                        type="radio"
                        name="signup-role"
                        value="owner"
                        checked={signupRole === 'owner'}
                        onChange={(e) => setSignupRole(e.target.value as 'owner')}
                        className="w-4 h-4"
                      />
                      <div className="font-medium text-foreground lowercase">course owner</div>
                    </label>
                  </div>
                </div>

                {WAITLIST_MODE && signupRole === 'player' && (
                  <div className="space-y-2">
                    <label htmlFor="signup-player-state" className="block text-sm font-medium text-foreground lowercase">
                      your state
                    </label>
                    <select
                      id="signup-player-state"
                      value={signupPlayerState}
                      onChange={(e) => setSignupPlayerState(e.target.value)}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all bg-white"
                    >
                      <option value="">select your state…</option>
                      {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                )}

                {signupRole === 'owner' && (
                  <div className="space-y-4 pt-6 border-t border-gray-100">
                    <div className="space-y-2">
                      <label htmlFor="signup-course-name" className="block text-sm font-medium text-foreground lowercase">
                        course name
                      </label>
                      <input
                        id="signup-course-name"
                        type="text"
                        placeholder="pineview disc golf"
                        value={signupCourseName}
                        onChange={(e) => setSignupCourseName(e.target.value)}
                        required={signupRole === 'owner'}
                        className="w-full px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="signup-holes" className="block text-sm font-medium text-foreground lowercase">
                        number of holes
                      </label>
                      <select
                        id="signup-holes"
                        value={signupHoles}
                        onChange={(e) => setSignupHoles(e.target.value)}
                        required={signupRole === 'owner'}
                        className="w-full px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all bg-white"
                      >
                        <option value="9">9 Holes</option>
                        <option value="12">12 Holes</option>
                        <option value="15">15 Holes</option>
                        <option value="18">18 Holes</option>
                        <option value="21">21 Holes</option>
                        <option value="24">24 Holes</option>
                        <option value="27">27 Holes</option>
                        <option value="30">30 Holes</option>
                        <option value="33">33 Holes</option>
                        <option value="36">36 Holes</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="signup-address" className="block text-sm font-medium text-foreground lowercase">
                        street address
                      </label>
                      <input
                        id="signup-address"
                        type="text"
                        placeholder="123 disc golf lane"
                        value={signupAddress}
                        onChange={(e) => setSignupAddress(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="signup-city" className="block text-sm font-medium text-foreground lowercase">
                        city
                      </label>
                      <input
                        id="signup-city"
                        type="text"
                        placeholder="portland"
                        value={signupCity}
                        onChange={(e) => setSignupCity(e.target.value)}
                        required={signupRole === 'owner'}
                        className="w-full px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="signup-state" className="block text-sm font-medium text-foreground lowercase">
                        state / province
                      </label>
                      <select
                        id="signup-state"
                        value={signupState}
                        onChange={(e) => setSignupState(e.target.value)}
                        required={signupRole === 'owner'}
                        className="w-full px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all bg-white"
                      >
                        <option value="">select…</option>
                        <optgroup label="US States">
                          {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                        </optgroup>
                        <optgroup label="Canadian Provinces">
                          {CA_PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                        </optgroup>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="signup-country" className="block text-sm font-medium text-foreground lowercase">
                        country
                      </label>
                      <select
                        id="signup-country"
                        value={signupCountry}
                        onChange={(e) => setSignupCountry(e.target.value)}
                        required={signupRole === 'owner'}
                        className="w-full px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all bg-white"
                      >
                        <option value="">select…</option>
                        {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                )}
                {error && (
                  <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
                    {error}
                  </div>
                )}
                {successMessage && (
                  <div className="p-3 text-sm text-green-600 bg-green-50 border border-green-200 rounded-lg">
                    {successMessage}
                  </div>
                )}
                <button
                  type="submit"
                  className="w-full px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-full font-medium transition-all duration-300 hover:shadow-lg hover:shadow-primary/25 lowercase"
                  disabled={loading}
                >
                  {loading ? 'creating account...' : 'sign up'}
                </button>
                <p className="text-xs text-center text-muted-foreground mt-3">
                  by creating an account, you agree to our{' '}
                  <Link to="/terms-of-service" className="underline hover:text-foreground transition-colors">terms of service</Link>
                  {' '}and{' '}
                  <Link to="/privacy-policy" className="underline hover:text-foreground transition-colors">privacy policy</Link>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
